/**
 * lib/identifiers.ts — THE canonical identifier definitions. One version,
 * everywhere, forever.
 *
 * Owner rulings 08/22/2026, verbatim: the three one-line definitions below,
 * and "fyi should be like this everywhere. no different versions" — with the
 * expansion always carrying "Fridge.com". The footer's bullet list is the
 * same text; the live Supabase comments on canonical_products.fin (and the
 * staged FPN/FFN mint migrations) lead with the same sentences.
 *
 * The rule this module enforces by construction: every surface that DEFINES
 * an identifier renders these strings — imported, never retyped. Surfaces
 * that cannot import (public/llms.txt) are pinned to the same strings by
 * tests/identifier-canonical-definitions.test.ts. A surface may append
 * mechanics AFTER the canonical sentence (format, mint function, never-rules)
 * — it may never vary the definition itself.
 *
 * Always named in this order: FIN, FPN, FFN, FIS — three permanent
 * identifiers, then the one score. A filter is not a part.
 */

export const IDENTIFIER_DEFINITIONS = {
  FIN:
    'FIN — Fridge.com Identification Number: the permanent canonical ID that links each appliance across retailers, covering prices, specifications, and recall history.',
  FPN:
    'FPN — Fridge.com Part Number: the permanent ID for a refrigerator or freezer replacement part, including gaskets and seals, sensors, hinges, handles, shelves and bins.',
  FFN:
    'FFN — Fridge.com Filter Number: the permanent ID for a refrigerator or freezer filter, including water filters, air filters and inline filters for refrigeration.',
  FIS:
    'FIS — Fridge.com Intelligence Score: a 0–100 rating of how well every U.S. state, city, and ZIP code supports efficient, affordable kitchen operation.',
} as const

export type IdentifierCode = keyof typeof IDENTIFIER_DEFINITIONS

/** The definition BODY — everything after "CODE — Expansion: ". For surfaces
 * that render the name as their own heading/strong and the body as prose. */
export function identifierDefinitionBody(code: IdentifierCode): string {
  const full = IDENTIFIER_DEFINITIONS[code]
  return full.slice(full.indexOf(': ') + 2)
}

/**
 * The body as a standalone sentence — first letter capitalised — for the
 * places that print it on its own line (page subtitles, the About cards).
 * The canonical sentence itself is unchanged; only the standalone rendering
 * starts with a capital (FRIDGE-1031: "the permanent canonical ID…" read as a
 * copy error at the top of every About identifier page).
 */
export function identifierDefinitionSentence(code: IdentifierCode): string {
  const body = identifierDefinitionBody(code)
  return body.charAt(0).toUpperCase() + body.slice(1)
}

/** The canonical name — "CODE — Fridge.com <Expansion>". */
export function identifierName(code: IdentifierCode): string {
  const full = IDENTIFIER_DEFINITIONS[code]
  return full.slice(0, full.indexOf(':'))
}

/**
 * FIS token — FRIDGE-1414 (owner, 10/05/2026: "FIS … should be like FIS-xxxx,
 * this way FIS is in there"). Every scored place carries an FIS token so the
 * score reads as part of the identifier family, while the 0–100 value stays a
 * score. The token names the PLACE, never the score: a score changes with its
 * inputs, a place does not, which is why the token is built from the place and
 * the score is printed beside it ("FIS-78701 · 72/100").
 *
 *   FIS-78701        a ZIP code (the five digits)
 *   FIS-TX           a state (the two-letter code)
 *   FIS-TX-AUSTIN    a city or town (the state code, then the city slug)
 */
export type FisScope =
  | { kind: 'zip'; zip: string }
  | { kind: 'state'; stateCode: string }
  | { kind: 'city'; stateCode: string; citySlug: string }

export const FIS_TOKEN_RULE =
  'Every scored place carries an FIS token that names the place: FIS-78701 for a ZIP code, FIS-TX for a state, FIS-TX-AUSTIN for a city or town. The 0–100 value beside it is the score.'

export function fisToken(scope: FisScope): string {
  if (scope.kind === 'zip') {
    const zip = scope.zip.trim()
    if (!/^\d{5}$/.test(zip)) throw new Error(`FIS token: a ZIP code is five digits, got "${scope.zip}"`)
    return `FIS-${zip}`
  }
  const state = scope.stateCode.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(state)) throw new Error(`FIS token: a state code is two letters, got "${scope.stateCode}"`)
  if (scope.kind === 'state') return `FIS-${state}`
  const city = scope.citySlug.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '')
  if (!city) throw new Error(`FIS token: a city slug is required, got "${scope.citySlug}"`)
  return `FIS-${state}-${city}`
}
